/**
 * Color Theme Generator
 * Generates complete color themes based on Accent and Supporting Accent colors
 * Supports both Light and Dark modes with HSL transformations
 */

/**
 * Convert HEX to HSL
 */
export const hexToHSL = (hex) => {
    // Remove # if present
    hex = hex.replace('#', '');

    // Convert to RGB
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h;
    let s;
    const l = (max + min) / 2;

    if (max === min) {
        h = 0;
        s = 0; // achromatic
    } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

        switch (max) {
            case r:
                h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
                break;
            case g:
                h = ((b - r) / d + 2) / 6;
                break;
            case b:
                h = ((r - g) / d + 4) / 6;
                break;
            default:
                h = 0;
        }
    }

    return {
        h: Math.round(h * 360),
        s: Math.round(s * 100),
        l: Math.round(l * 100),
    };
};

/**
 * Convert HSL to HEX
 */
export const hslToHex = (h, s, l) => {
    s /= 100;
    l /= 100;

    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;
    let r = 0;
    let g = 0;
    let b = 0;

    if (h >= 0 && h < 60) {
        r = c;
        g = x;
        b = 0;
    } else if (h >= 60 && h < 120) {
        r = x;
        g = c;
        b = 0;
    } else if (h >= 120 && h < 180) {
        r = 0;
        g = c;
        b = x;
    } else if (h >= 180 && h < 240) {
        r = 0;
        g = x;
        b = c;
    } else if (h >= 240 && h < 300) {
        r = x;
        g = 0;
        b = c;
    } else if (h >= 300 && h < 360) {
        r = c;
        g = 0;
        b = x;
    }

    const rHex = Math.round((r + m) * 255)
        .toString(16)
        .padStart(2, '0');
    const gHex = Math.round((g + m) * 255)
        .toString(16)
        .padStart(2, '0');
    const bHex = Math.round((b + m) * 255)
        .toString(16)
        .padStart(2, '0');

    return `#${rHex}${gHex}${bHex}`.toUpperCase();
};

/**
 * Adjust HSL values
 */
const adjustHSL = (hsl, hueChange = 0, satChange = 0, lightChange = 0) => ({
    h: (hsl.h + hueChange + 360) % 360,
    s: Math.max(0, Math.min(100, hsl.s + satChange)),
    l: Math.max(0, Math.min(100, hsl.l + lightChange)),
});

/**
 * Calculate appropriate text color based on background color lightness
 * Returns dark text for light backgrounds and light text for dark backgrounds
 * @param {string} backgroundColor - HEX color code
 * @returns {string} HEX color code for text
 */
export const calculateTextColorForBackground = (backgroundColor) => {
    const bgHSL = hexToHSL(backgroundColor);

    // If background lightness is greater than 50, use dark text (lightness 10)
    // Otherwise use light text (lightness 90)
    const textLightness = bgHSL.l > 50 ? 10 : 90;

    return hslToHex(bgHSL.h, bgHSL.s, textLightness);
};

/**
 * Generate Light Theme Colors based on Accent color
 */
export const generateLightThemeColors = (accentHex) => {
    const accentHSL = hexToHSL(accentHex);

    // Page Background: Accent HSL → Change Lightness (L) to 97
    const backgroundColor = hslToHex(accentHSL.h, accentHSL.s, 97);

    return {
        // Primary colors
        accentColor: accentHex,

        // Page Background
        backgroundColor,

        // Cards & Panels: Keep white
        cardsPanelsColor: '#FFFFFF',

        // Secondary Cards & Panels: Accent HSL → Change Lightness (L) to 96
        secondaryCardsPanelsColor: hslToHex(accentHSL.h, accentHSL.s, 96),

        // Text & Icons on Secondary Cards: Accent HSL → Change Lightness (L) to 14
        textAndIconOnSecondaryCardsPanelsColor: hslToHex(
            accentHSL.h,
            accentHSL.s,
            14
        ),

        // Lines & Dividers: Accent HSL → Change Lightness (L) to 94
        linesDividersColor: hslToHex(accentHSL.h, accentHSL.s, 94),

        // Alerts & Warnings: Accent HSL → Change Hue (H) to 4
        alertsWarningsColor: hslToHex(4, accentHSL.s, accentHSL.l),

        // Confirmations: Accent HSL → Change Hue (H) to 124
        confirmationsColor: hslToHex(124, accentHSL.s, accentHSL.l),

        // Text & Icons on Background: Automatically calculated based on background lightness
        textAndIconOnBackgroundColor:
            calculateTextColorForBackground(backgroundColor),

        // Text & Icons on Cards: Accent HSL → Change Lightness (L) to 10
        textAndIconOnCardColor: hslToHex(accentHSL.h, accentHSL.s, 10),

        // Text & Icons on Accent: Keep white
        textAndIconOnAccentColor: '#FFFFFF',

        // Text & Icons on Alerts & Warnings: Keep white
        textAndIconOnAlertsWarningsColor: '#FFFFFF',
    };
};

/**
 * Generate Dark Theme Colors based on Accent color
 */
export const generateDarkThemeColors = (accentHex) => {
    const accentHSL = hexToHSL(accentHex);

    // Page Background: Accent HSL → Change Lightness (L) to 8
    const backgroundColor = hslToHex(accentHSL.h, accentHSL.s, 8);

    return {
        // Primary colors
        accentColor: accentHex,

        // Page Background
        backgroundColor,

        // Cards & Panels: Accent HSL → Change Lightness (L) to 14
        cardsPanelsColor: hslToHex(accentHSL.h, accentHSL.s, 14),

        // Secondary Cards & Panels: Accent HSL → Change Lightness (L) to 18
        secondaryCardsPanelsColor: hslToHex(accentHSL.h, accentHSL.s, 18),

        // Text & Icons on Secondary Cards: Accent HSL → Change Lightness (L) to 96
        textAndIconOnSecondaryCardsPanelsColor: hslToHex(
            accentHSL.h,
            accentHSL.s,
            96
        ),

        // Lines & Dividers: Accent HSL → Change Lightness (L) to 20
        linesDividersColor: hslToHex(accentHSL.h, accentHSL.s, 20),

        // Alerts & Warnings: Accent HSL → Change Hue (H) to 10
        alertsWarningsColor: hslToHex(10, accentHSL.s, accentHSL.l),

        // Confirmations: Accent HSL → Change Hue (H) to 124
        confirmationsColor: hslToHex(124, accentHSL.s, accentHSL.l),

        // Text & Icons on Background: Automatically calculated based on background lightness
        textAndIconOnBackgroundColor:
            calculateTextColorForBackground(backgroundColor),

        // Text & Icons on Cards: Accent HSL → Change Lightness (L) to 90
        textAndIconOnCardColor: hslToHex(accentHSL.h, accentHSL.s, 90),

        // Text & Icons on Accent: Keep white
        textAndIconOnAccentColor: '#FFFFFF',

        // Text & Icons on Alerts & Warnings: Keep white
        textAndIconOnAlertsWarningsColor: hslToHex(
            accentHSL.h,
            accentHSL.s,
            14
        ),
    };
};

/**
 * Derive light & dark background colors from an accent color.
 * Used to populate PREDEFINED_THEMES so theme icons show the page background.
 */
const getThemeBackgroundColors = (accentHex) => ({
    light: generateLightThemeColors(accentHex).backgroundColor,
    dark: generateDarkThemeColors(accentHex).backgroundColor,
});

/**
 * Predefined Color Themes
 * Each theme stores its accent color (for generation) and precomputed
 * background colors for both modes (for display in the theme picker).
 */
export const PREDEFINED_THEMES = [
    {
        id: 'blue',
        name: 'Blue',
        accentColor: '#0054FF',
        backgroundColor: getThemeBackgroundColors('#0054FF'),
    },
    {
        id: 'purple',
        name: 'Purple',
        accentColor: '#8B5CF6',
        backgroundColor: getThemeBackgroundColors('#8B5CF6'),
    },
    {
        id: 'orange',
        name: 'Orange',
        accentColor: '#F97316',
        backgroundColor: getThemeBackgroundColors('#F97316'),
    },
    {
        id: 'pink',
        name: 'Pink',
        accentColor: '#EC4899',
        backgroundColor: getThemeBackgroundColors('#EC4899'),
    },
    {
        id: 'teal',
        name: 'Teal',
        accentColor: '#14B8A6',
        backgroundColor: getThemeBackgroundColors('#14B8A6'),
    },
    {
        id: 'indigo',
        name: 'Indigo',
        accentColor: '#6366F1',
        backgroundColor: getThemeBackgroundColors('#6366F1'),
    },
    {
        id: 'green',
        name: 'Green',
        accentColor: '#10B981',
        backgroundColor: getThemeBackgroundColors('#10B981'),
    },
    {
        id: 'custom',
        name: 'Custom',
        accentColor: null,
        backgroundColor: null,
        isCustom: true,
    },
];

/**
 * Generate complete theme colors for both light and dark modes
 */
export const generateCompleteTheme = (accentColor) => ({
    light: generateLightThemeColors(accentColor),
    dark: generateDarkThemeColors(accentColor),
});

/**
 * Derive accent color from a background color.
 *
 * Only the Hue (H) is taken from the background. Saturation (S) and
 * Lightness (L) are preserved from the current accent because at extreme
 * lightness (L=97 light / L=8 dark) the HEX representation severely
 * compresses saturation information, making a full round-trip unreliable.
 *
 * @param {string} backgroundHex - The background color chosen by the user
 * @param {number} accentSaturation - The Saturation value (0-100) of the current accent
 * @param {number} accentLightness  - The Lightness value (0-100) of the current accent
 * @returns {string} Derived accent color in HEX
 */
export const deriveAccentFromBackground = (
    backgroundHex,
    accentSaturation,
    accentLightness
) => {
    const bgHSL = hexToHSL(backgroundHex);
    return hslToHex(bgHSL.h, accentSaturation, accentLightness);
};

/**
 * Generate a complete theme using a background color as the primary input.
 *
 * 1. Derives an accent color from the background (hue from background,
 *    saturation & lightness from the reference accent).
 * 2. Generates full light + dark themes from the derived accent.
 * 3. Overrides the active mode's backgroundColor with the exact value the
 *    user chose, and recalculates textAndIconOnBackgroundColor accordingly.
 *
 * @param {string} backgroundHex        - The user's chosen background color
 * @param {number} referenceSaturation   - Saturation (0-100) from the current/reference accent
 * @param {number} referenceLightness    - Lightness  (0-100) from the current/reference accent
 * @param {string} activeMode            - 'light' | 'dark'
 * @returns {{ theme: { light: object, dark: object }, derivedAccent: string }}
 */
export const generateThemeFromBackground = (
    backgroundHex,
    referenceSaturation,
    referenceLightness,
    activeMode
) => {
    const derivedAccent = deriveAccentFromBackground(
        backgroundHex,
        referenceSaturation,
        referenceLightness
    );

    const theme = generateCompleteTheme(derivedAccent);

    // Preserve the user's exact background for the active mode
    theme[activeMode].backgroundColor = backgroundHex;
    theme[activeMode].textAndIconOnBackgroundColor =
        calculateTextColorForBackground(backgroundHex);

    return { theme, derivedAccent };
};
